import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { sliderPortrait } from "../../data/films";
import { AI_FILTER, scanPins } from "../../data/notell";
import { useComparisonFrame } from "./useComparisonFrame";
import { Timecode } from "./Timecode";
const SWEEP_SECONDS = 1.6;
const EASE = [0.23, 1, 0.32, 1] as const;
export function ComparisonFrame() {
  const {
    pos,
    frameRef,
    frozen,
    scanStarted,
    reduce,
    onKeyDown,
    pointerHandlers
  } = useComparisonFrame();
  const driftClass = `scene-drift ${frozen ? "is-held" : ""}`;
  return <div data-id="element-222" data-mp="2pkpgu2knt2ve">
      <div ref={frameRef} className="relative aspect-video w-full cursor-ew-resize select-none overflow-hidden bg-card" style={{
      touchAction: "pan-y"
    }} {...pointerHandlers} data-id="element-223" data-mp="2pkh4qcwd21l9">
        {/* Hero side: natural grade with grain */}
        <img src={sliderPortrait.src} alt={`${sliderPortrait.alt}, natural Brandhero AI grade`} draggable={false} className={`absolute inset-0 h-full w-full object-cover ${driftClass}`} data-id="element-224" data-mp="10597am2tpf4z" />
        <div className="grain pointer-events-none absolute inset-0" aria-hidden="true" data-id="element-225" data-mp="ue9ehpdiyo5a" />

        {/* Villain side: typical AI look */}
        <div className="absolute inset-0" style={{
        clipPath: `inset(0 ${100 - pos}% 0 0)`
      }} data-id="element-226" data-mp="peeqbtl4wcnx">
          <img src={sliderPortrait.src} alt="Same scene with over-smooth, plasticky skin and a glossy typical AI look" draggable={false} className={`h-full w-full object-cover ${driftClass}`} style={{
          filter: AI_FILTER
        }} data-id="element-227" data-mp="ledy965442ye" />
        </div>

        {/* Signal light in the clouds */}
        <div aria-hidden="true" className="signal-light pointer-events-none absolute left-[14%] top-[6%]" style={{
        transform: "perspective(500px) rotateX(24deg) skewX(-10deg)"
      }} data-id="element-228" data-mp="1959vwjw91sdk">
          <span className="font-anton text-[13px] tracking-[0.04em] text-signal md:text-[22px]" style={{
          textShadow: "0 0 10px rgba(255,73,80,0.9), 0 0 28px rgba(255,73,80,0.5)"
        }} data-id="element-229" data-mp="3oubarlqqxmzw">
            BRANDHERO
          </span>
        </div>

        {/* Comic panel keyline */}
        <div className="pointer-events-none absolute inset-2.5 border border-white/15 md:inset-4" aria-hidden="true" data-id="element-230" data-mp="3hb2bilbyf4ok" />

        {/* Scanner sweep */}
        {scanStarted && !reduce && <motion.div aria-hidden="true" className="pointer-events-none absolute inset-y-0 w-px bg-signal" style={{
        boxShadow: "0 0 12px 2px rgba(255,73,80,0.55)"
      }} initial={{
        left: "0%",
        opacity: 1
      }} animate={{
        left: "100%",
        opacity: [1, 1, 0]
      }} transition={{
        duration: SWEEP_SECONDS,
        ease: "linear",
        opacity: {
          duration: SWEEP_SECONDS,
          times: [0, 0.9, 1]
        }
      }} data-id="element-231" data-mp="37kz9jvmuuge3" />}

        {/* Visor pins */}
        {scanStarted && <ul aria-label="Director frame check" className="pointer-events-none absolute inset-0" data-id="element-232" data-mp="2tky6c9j4awvg">
            {scanPins.map(pin => {
          const passed = pin.x > pos;
          return <motion.li key={pin.id} className="absolute flex items-center" style={{
            left: `${pin.x}%`,
            top: `${pin.y}%`,
            flexDirection: pin.side === "left" ? "row-reverse" : "row"
          }} initial={{
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.2,
            ease: EASE,
            delay: reduce ? 0 : pin.x / 100 * SWEEP_SECONDS
          }} data-id="element-233" data-mp="250utwi25id1i">
                  <span className={`block h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 border ${passed ? "border-white bg-white/20" : "border-signal bg-signal/30"}`} aria-hidden="true" data-id="element-234" data-mp="32o3l8tou2gbf" />
                  <span className={`hidden h-px w-5 -translate-y-1/2 md:block ${passed ? "bg-white/60" : "bg-signal"}`} aria-hidden="true" data-id="element-235" data-mp="3bpjmaatrd62z" />
                  <span className={`hidden -translate-y-1/2 whitespace-nowrap px-1.5 py-0.5 font-mono text-[10px] tracking-[0.1em] md:block ${passed ? "bg-black/70 text-white" : "bg-signal text-ink"}`} data-id="element-236" data-mp="36nuagsypoodu">
                    {passed ? pin.pass : pin.fail}
                  </span>
                </motion.li>;
        })}
          </ul>}

        {/* Origin-story titles */}
        <div className="pointer-events-none absolute left-4 top-4 bg-black/65 px-3 py-2 md:left-7 md:top-7 md:px-4 md:py-3" data-id="element-237" data-mp="m3hrplysmw8z">
          <span className="block font-mono text-[9px] tracking-[0.18em] text-white/70 md:text-[10px]" data-id="element-238" data-mp="v2blz5onjajd">THE VILLAIN</span>
          <span className="block font-serif text-[20px] italic leading-none text-white md:text-[32px]" data-id="element-239" data-mp="101xzlg8e6ssp">Typical AI</span>
        </div>
        <div className="pointer-events-none absolute right-4 top-4 bg-signal px-3 py-2 text-right md:right-7 md:top-7 md:px-4 md:py-3" data-id="element-240" data-mp="26gp6c0k757un">
          <span className="block font-mono text-[9px] tracking-[0.18em] text-ink/80 md:text-[10px]" data-id="element-241" data-mp="2biekhp2pu1my">THE HERO</span>
          <span className="block font-serif text-[20px] italic leading-none text-ink md:text-[32px]" data-id="element-242" data-mp="2glp3c8fi59bf">Brandhero AI</span>
        </div>

        {/* Bottom bar */}
        <div className="pointer-events-none absolute inset-x-4 bottom-4 flex items-center justify-between md:inset-x-7 md:bottom-7" data-id="element-243" data-mp="2bsvzdmruy50e">
          <Timecode frozen={frozen} data-id="element-244" data-mp="22u0ss9kwiswk" />
          <span className="hidden font-mono text-[10px] tracking-[0.14em] text-white/70 sm:block md:text-[11px]" data-id="element-245" data-mp="22v21w8icx5bn">
            ISSUE 01 · AI FOOTAGE MEETS ITS HERO
          </span>
        </div>

        {/* Freeze-frame stamp */}
        <AnimatePresence data-id="element-246" data-mp="1sw4ifjzsg1zi">
          {frozen && <motion.div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/25" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.18,
          ease: EASE
        }} data-id="element-247" data-mp="1ourfpirzsewt">
              <motion.div role="status" className="-rotate-6 border-[3px] border-signal bg-black/60 px-5 py-3 text-center md:px-8 md:py-4" initial={{
            scale: reduce ? 1 : 1.15
          }} animate={{
            scale: 1
          }} transition={{
            duration: 0.18,
            ease: EASE
          }} data-id="element-248" data-mp="1gkk76icze7f9">
                <span className="block font-mono text-[18px] font-medium tracking-[0.16em] text-signal md:text-[32px]" data-id="element-249" data-mp="1bk5z50bdqxh8">TELL DETECTED</span>
                <span className="mt-1 block font-mono text-[9px] tracking-[0.14em] text-white/80 md:text-[11px]" data-id="element-250" data-mp="24bfwn8tysqjc">
                  FRAME HELD · DRAG BACK TO PLAY
                </span>
              </motion.div>
            </motion.div>}
        </AnimatePresence>

        {/* Divider + handle */}
        <div className="absolute inset-y-0 w-[2px] -translate-x-1/2 bg-signal" style={{
        left: `${pos}%`
      }} aria-hidden="true" data-id="element-251" data-mp="evzxmm9r8l76" />
        <div role="slider" tabIndex={0} aria-label="Compare the villain, typical AI, with the hero, Brandhero AI" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(pos)} aria-valuetext={frozen ? "Fully typical AI, frame held: tell detected" : `${Math.round(pos)}% typical AI`} onKeyDown={onKeyDown} className="absolute top-1/2 flex h-11 w-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center bg-signal font-mono text-[12px] font-medium tracking-[0.06em] text-ink md:h-12 md:w-12" style={{
        left: `${pos}%`
      }} data-id="element-252" data-mp="jwxemvj13973">
          VS
        </div>
      </div>
      <p className="mt-4 font-mono text-[11px] tracking-[0.1em] text-foot md:text-[12px]" data-id="element-253" data-mp="2efhwsa5wu4n">
        DRAG ALL THE WAY TO THE VILLAIN TO FREEZE THE FRAME.
      </p>
    </div>;
}